// LIFE · Mountain — the Compass in another view, drawn ON the shared orbit
// backdrop (see orbit.tsx; the home paints the mountain full-bleed). This
// component is only the overlay: the front face's own path with its five
// stops (Endeavour at the foot → Step at the summit), the sector names
// riding around the peak, the D-pad (◀ ▶ next face · ▲ ▼ climb · ❚❚ spin)
// and the sheet for the selected stop.
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Sector, Stage } from '../domain/olmps';
import type { OrbCtx } from '../olmps/CompassView';
import { formatLadderRungState } from '../olmps/useTodayData';
import { useSharedTodayData } from '../olmps/TodayDataContext';
import { NodePlacard, placardCopy } from './NodePlacard';
import { DPad } from './DPad';
import { NAV_D } from './EdgeTabs';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import { useAuth } from '../auth/AuthProvider';
import { useAlignment } from '../olmps/useAlignment';
import { ALIGNMENT_STATUS, type AlignmentOrb, type AlignmentStatus } from '../olmps/alignment';
import { goalTextOf } from '../olmps/atlas-data';
import { effectiveAims } from '../olmps/oracle-spine-data';
import type { NowStep } from './useCurrentStep';
import { FACE, SECTORS, type Orbit } from './orbit';
import { DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';
import { useTourTarget } from './onboarding';

const STAGES = ['Endeavour', 'Goal', 'Aim', 'Today', 'Step'] as const;
/** The alignment orb each stop reads (Step follows Today). */
const STOP_ORB: AlignmentOrb[] = ['endeavour', 'goal', 'aim', 'today', 'today'];
type Pt = { x: number; y: number };
// Each face has its OWN path up the mountain (fractions of the square frame).
const STOP_POS_BY_SECTOR: Record<Sector, Pt[]> = {
  S: [{ x: 0.5, y: 0.88 }, { x: 0.38, y: 0.76 }, { x: 0.58, y: 0.64 }, { x: 0.44, y: 0.51 }, { x: 0.5, y: 0.37 }],
  H: [{ x: 0.56, y: 0.88 }, { x: 0.66, y: 0.76 }, { x: 0.56, y: 0.64 }, { x: 0.6, y: 0.5 }, { x: 0.5, y: 0.37 }],
  A: [{ x: 0.64, y: 0.88 }, { x: 0.7, y: 0.74 }, { x: 0.58, y: 0.63 }, { x: 0.62, y: 0.5 }, { x: 0.5, y: 0.37 }],
  P: [{ x: 0.42, y: 0.87 }, { x: 0.54, y: 0.77 }, { x: 0.44, y: 0.66 }, { x: 0.54, y: 0.53 }, { x: 0.5, y: 0.37 }],
  E: [{ x: 0.7, y: 0.86 }, { x: 0.54, y: 0.78 }, { x: 0.66, y: 0.66 }, { x: 0.48, y: 0.54 }, { x: 0.5, y: 0.37 }],
};

export function MountainView({
  orbit,
  frameTop,
  frameSize,
  frameLeft = 0,
  projects,
  spine,
  current,
  onOpenTool,
  onOrbCtx,
  onExplore,
}: {
  orbit: Orbit;
  /** Top of the square orbit frame, in this view's coordinates. */
  frameTop: number;
  frameSize: number;
  /** Frame's left edge on screen (negative when the frame is wider than the screen). */
  frameLeft?: number;
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  current: NowStep | null;
  onOpenTool: (tool: string) => void;
  onOrbCtx: (ctx: OrbCtx) => void;
  /** "Explore ▼" — pan down to the orb on the selected sector/stage. */
  onExplore: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  // Stops are drawn inside one frame and the Explore button lives in the placard: the frame + placard are the 'point' target.
  const pointFrame = useTourTarget('mountain.point');
  const pointPlacard = useTourTarget('mountain.point');
  const { width: winW } = useWindowDimensions();
  // The frame can be wider than the screen; keep the D-pad on the visible right edge.
  const dpadRight = Math.max(4, frameSize - (winW - frameLeft) + 16);
  const W = frameSize;
  const H = frameSize;
  const [stageIdx, setStageIdx] = useState(4);
  const [placardOn, setPlacardOn] = useState(false);
  const { ladder } = useSharedTodayData();
  const { session } = useAuth();
  const { board } = useAlignment(session?.user.id);
  const pickStop = (i: number) => {
    setStageIdx(i);
    setPlacardOn(true);
  };
  const { frontSector, frontDeg, settled, spinning } = orbit;

  const node = spine?.[frontSector] ?? null;
  const list = projects[frontSector] ?? [];
  // Drift colouring is the front sector's Alignment Score (Aim orb = the Home number); each stop wears its own orb's status.
  const aim = board[frontSector].aim;
  const STATUS_COLOR: Record<AlignmentStatus, string> = { aligned: t.onPace, mostly: '#7fc48b', drifting: t.behind, misaligned: t.lapsed, neglected: t.lapsed, unset: t.ivoryFaint };
  // Light climbs the path only through orbs on the centre line: Step is the base, so count on-line stops upward.
  let lit = 0;
  for (let j = STAGES.length - 1; j >= 0 && board[frontSector][STOP_ORB[j]].segments === 0; j--) lit += 1;
  const nextSteps = list.map((p) => p.steps.find((s) => s.status !== 'C')).filter(Boolean) as { text: string }[];
  const stageText: (string | null)[] = [
    node?.endeavour || null,
    goalTextOf(node) || null,
    (node ? effectiveAims(node).map((a) => a.label).join(' · ') : '') || null,
    list.length ? `${list.length} project${list.length === 1 ? '' : 's'} in play${aim.shown == null ? '' : ` · ${ALIGNMENT_STATUS[aim.status].word.toLowerCase()}`}` : null,
    (current?.sector === frontSector ? current.title : nextSteps[0]?.text) || null,
  ];

  // Sector names ride an ellipse around the peak; a = 0 is the front.
  const markers = useMemo(() => {
    const cx = W * 0.5;
    const cy = H * 0.62;
    const rx = Math.max(60, W * 0.5 - 56); // keep the widest label (ECONOMIC) fully inside the frame
    const ry = H * 0.12;
    return SECTORS.map((s, i) => {
      const a = ((i * FACE - frontDeg) * Math.PI) / 180;
      return { s, x: cx + Math.sin(a) * rx, y: cy + Math.cos(a) * ry, depth: (Math.cos(a) + 1) / 2 };
    });
  }, [W, H, frontDeg]);

  const MSTAGE: Stage[] = ['E', 'G', 'A', 'T', 'S'];
  const RUNG = ['endeavour', 'goal', 'aim', 'today', 'step'] as const;
  const rungState = formatLadderRungState(ladder.rungs[RUNG[stageIdx]]);
  const copy = placardCopy(MSTAGE[stageIdx], SECTOR_NAME[frontSector], stageText[stageIdx], ladder.rungs[RUNG[stageIdx]]);
  // A drifted stop: ATLAS says why, in place of the pace line.
  const stopAl = board[frontSector][STOP_ORB[stageIdx]];
  if (stopAl.segments != null && stopAl.segments > 0) copy.state = stopAl.reason;
  useEffect(() => {
    onOrbCtx({ spokeLabel: SECTOR_NAME[frontSector], sector: frontSector, stage: MSTAGE[stageIdx], stageLabel: STAGES[stageIdx], rungState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontSector, stageIdx, rungState]);
  // Endeavour at the summit, Step at the base (Joel, 2026-10-07): stage index 0 = Endeavour takes the top stop.
  const path = STOP_POS_BY_SECTOR[frontSector].slice().reverse();
  return (
    <View style={styles.root}>
      <View ref={pointFrame.onLayoutRef} style={[styles.frame, { top: frameTop, width: W, height: H }]} {...orbit.panHandlers}>
        {markers
          .slice()
          .sort((a, b) => a.depth - b.depth)
          .map((m) => (
            <Pressable key={m.s} onPress={() => orbit.snapToFace(SECTORS.indexOf(m.s))} hitSlop={8} style={[styles.marker, { left: m.x - 50, top: m.y - 14, opacity: m.depth > 0.92 ? 0 : 0.62 + m.depth * 0.38, transform: [{ scale: 0.8 + m.depth * 0.3 }] }]}>
              <Text numberOfLines={1} style={[styles.markerText, { color: SECTOR_COLOR[m.s] }]}>{SECTOR_NAME[m.s].toUpperCase()}</Text>
            </Pressable>
          ))}

        {settled ? (
          <>
            {/* Below the header icons and the ▲ circle, so it never runs under them. */}
            <View pointerEvents="none" style={[styles.faceTitleWrap, { top: insets.top + 52 + 6 + NAV_D + 4 }]}>
              <Text style={[styles.faceTitle, { color: SECTOR_COLOR[frontSector] }]}>{SECTOR_NAME[frontSector].toUpperCase()} FACE</Text>
            </View>
            {path.map((p, i) => {
              if (i === 0) return null;
              const q = path[i - 1];
              // Stop i-1 sits above stop i; the light has climbed to this segment only if both stops are on the line.
              const climbed = i - 1 >= STAGES.length - lit;
              return <View key={`seg-${i}`} pointerEvents="none" style={[styles.seg, climbed ? { backgroundColor: SECTOR_COLOR[frontSector], boxShadow: `0 0 10px ${SECTOR_COLOR[frontSector]}` } : { backgroundColor: t.ivoryFaint, opacity: 0.35 }, segStyle(q.x * W, q.y * H, p.x * W, p.y * H)]} />;
            })}
            {path.map((p, i) => {
              const on = i === stageIdx;
              return (
                <Pressable key={STAGES[i]} onPress={() => pickStop(i)} hitSlop={10} style={[styles.stop, { left: p.x * W - 13, top: p.y * H - 13 }]}>
                  <View style={[styles.stopDot, board[frontSector][STOP_ORB[i]].segments != null && { borderColor: STATUS_COLOR[board[frontSector][STOP_ORB[i]].status] }, on && styles.stopDotOn, on && ({ boxShadow: `0 0 18px ${t.goldGlow}` } as object)]} />
                  <Text style={[styles.stopLabel, on && { color: t.goldBright }]}>{STAGES[i].toUpperCase()}</Text>
                </Pressable>
              );
            })}
          </>
        ) : null}

        <DPad
          style={{ right: dpadRight, top: H * 0.37 + 36 }}
          onUp={() => pickStop(Math.max(0, stageIdx - 1))}
          onDown={() => pickStop(Math.min(4, stageIdx + 1))}
          onLeft={() => {
            setPlacardOn(false);
            orbit.turn(-1);
          }}
          onRight={() => {
            setPlacardOn(false);
            orbit.turn(1);
          }}
          onCenter={orbit.toggleSpin}
          centerGlyph={spinning ? '❚❚' : '▶'}
        />
      </View>

      {placardOn ? (
        <NodePlacard
          tourRef={pointPlacard.onLayoutRef}
          kicker={`${SECTOR_NAME[frontSector]} · ${STAGES[stageIdx]}`}
          text={copy.text}
          state={copy.state}
          onExplore={() => {
            setPlacardOn(false);
            onExplore();
          }}
        />
      ) : null}
    </View>
  );
}

function segStyle(x1: number, y1: number, x2: number, y2: number) {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const ang = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  return { left: (x1 + x2) / 2 - len / 2, top: (y1 + y2) / 2, width: len, transform: [{ rotate: `${ang}deg` }] };
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1 },
    frame: { position: 'absolute', left: 0 },
    marker: { position: 'absolute', width: 100, alignItems: 'center', outlineStyle: 'none' } as object,
    markerText: { fontFamily: DISPLAY_FONT, fontSize: 13, fontWeight: '800', letterSpacing: 1.5, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8, overflow: 'hidden', backgroundColor: t.mode === 'dark' ? 'rgba(7,11,23,0.55)' : 'rgba(255,255,255,0.6)', textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,1)' : 'rgba(255,255,255,1)', textShadowRadius: 8 },
    seg: { position: 'absolute', height: 2.5, opacity: 0.85 } as object,
    faceTitleWrap: { position: 'absolute', top: 4, left: 0, right: 0, alignItems: 'center', gap: 2 },
    faceTitle: { fontFamily: DISPLAY_FONT, fontSize: 18, letterSpacing: 3, textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)', textShadowRadius: 8 },
    stop: { position: 'absolute', width: 26, alignItems: 'center', outlineStyle: 'none' } as object,
    stopDot: { width: 14, height: 14, borderRadius: 7, borderWidth: 2, borderColor: t.goldBright, backgroundColor: t.mode === 'dark' ? 'rgba(7,11,23,0.7)' : 'rgba(255,255,255,0.85)', marginTop: 6 },
    stopDotOn: { width: 22, height: 22, borderRadius: 11, backgroundColor: t.gold, marginTop: 2 },
    stopLabel: { color: t.ivory, fontSize: 8.5, fontWeight: '800', letterSpacing: 1.2, marginTop: 3, width: 70, textAlign: 'center', textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)', textShadowRadius: 6 },
  }),
);
